import { LocaleContext } from '../../../components/LocaleContext';
import React, { useContext, useEffect, useState } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { getRequest, postRequest } from '../../../api/ApiController';
import { SaveIcon, VerticalLineIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon } from "../../svg/svgIcons";
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { Dropdown } from 'primereact/dropdown';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import {
  emitCalibrationCompanyListMutation,
  unwrapCalibrationCompanyPayload,
} from '../../../utils/calibrationCompanyListSync';

export default function CalibrationCompanyCertificateAdd({ calibration_company, type }) {
    const { t } = useContext(LocaleContext);

    const [state, dispatch] = useContext(DatabaseContext);
    const { openFavoriteDialog, toast, setFastView } = useContext(UtilContext);
    const { user } = useContext(AuthContext);

    const company = React.useMemo(
        () => state.calibration_companies?.find(c => c.id === calibration_company?.id) ?? calibration_company,
        [state.calibration_companies, calibration_company]
    );

    const [formType] = useState(type);
    const [companyValidity, setCompanyValidity] = useState(1); // default Aktívny
    const [savingCompanyValidity, setSavingCompanyValidity] = useState(false);

    useEffect(() => {
        // Ak má firma explicitne 0, zobraz Vyradený, inak Aktívny (vrátane prázdneho/nového)
        setCompanyValidity(company?.validity === 0 || company?.validity === '0' ? 0 : 1);
    }, [company?.id, company?.validity]);

    const handleSaveCompanyValidity = (closeAfter = false) => {
        if (!company?.id) return;
        setSavingCompanyValidity(true);
        const formData = new FormData();
        formData.append('validity', companyValidity);
        postRequest(`/api/companies/update/${company.id}`, formData, true)
            .then((saveRes) => Promise.all([
                Promise.resolve(saveRes),
                getRequest('/api/companies'),
            ]))
            .then(([saveRes, companiesRes]) => {
                dispatch({ type: 'load_companies', newCompanies: companiesRes.data });
                const saved = unwrapCalibrationCompanyPayload(saveRes)
                    || unwrapCalibrationCompanyPayload(companiesRes)
                    || ((Array.isArray(companiesRes?.data) ? companiesRes.data : []).find((c) => String(c?.id) === String(company.id)) || null);
                const prevAt = company?.updated_at;
                const nextAt = saved?.updated_at;
                const unchanged = prevAt != null && nextAt != null && String(prevAt) === String(nextAt)
                    && String(company?.validity ?? '') === String(companyValidity);
                if (unchanged) {
                    emitCalibrationCompanyListMutation({
                        operation: 'noop',
                        id: company.id,
                        row: saved,
                        changedFields: [],
                    });
                } else {
                    emitCalibrationCompanyListMutation({
                        operation: 'update',
                        id: company.id,
                        row: saved,
                        changedFields: ['validity', 'company_status_display'],
                    });
                }
                toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Calibration.CompanyStatusSaved') });
                if (closeAfter) dispatch({ type: 'close_tab', id: state.selectedTab });
            })
            .catch((reason) => {
                console.error(reason);
                toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: t('Common.SaveFailed') });
            })
            .finally(() => setSavingCompanyValidity(false));
    };

    const insertCopyIcon = !user.permissions?.includes("edit calibration-company");

    return (
        <>
            <div>
                <div className='w-full h-10 flex flex-row bg-[#333]'>
                    <IntersectionObserverWrapper>
                        <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="saveandclose">
                            <button className="disabled:opacity-25" disabled={insertCopyIcon || savingCompanyValidity} onClick={() => handleSaveCompanyValidity(true)}>
                                <SaveAndCloseIcon/>
                            </button>
                        </div>
                        <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="save">
                            <button className="disabled:opacity-25" disabled={insertCopyIcon || savingCompanyValidity} onClick={() => handleSaveCompanyValidity()}>
                                <SaveIcon/>
                            </button>
                        </div>
                        <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
                        <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="favorite">
                            <button className="disabled:opacity-25" onClick={() => openFavoriteDialog(t('Calibration.CompanyCard'), "CalibrationCompanyCertificateAdd")}>
                                <FavoriteIcon/>
                            </button>
                        </div>
                        <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="price">
                            <button onClick={() => setFastView("CalibrationCompanyCertificateAdd", {}, [])}>
                                <PriceIcon/>
                            </button>
                        </div>
                        <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
                    </IntersectionObserverWrapper>
                </div>
                <div className="w-full relative flex flex-col min-h-[calc(100vh_-_177px)] overflow-x overflow-y-auto px-4 py-4">
                    <div className='xl:grid grid-cols-2 gap-1 mb-2 '>
                        <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                            <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('Ui.R67.KARTA_KALIBRACNEJ_FIRMY')}</h1>
                            {formType === "edit" && <label className="block text-md font-semibold text-gray-800">Por.č.: {company?.id}</label>}
                            <div>
                                <label htmlFor="name" className="block text-md font-semibold text-gray-800">{t('Ui.R67.NAZOV_KALIBRACNEJ_FIRMY')}</label>
                                <input
                                    readOnly={true}
                                    name='name'
                                    type="text"
                                    value={company?.name ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label htmlFor="tel_number" className="block text-md font-semibold text-gray-800">{t('Common.PhoneNumber')}</label>
                                <input
                                    readOnly={true}
                                    name='tel_number'
                                    type="text"
                                    value={company?.tel_number ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-md font-semibold text-gray-800">E-MAIL</label>
                                <input
                                    readOnly={true}
                                    name='email'
                                    type="text"
                                    value={company?.email ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label htmlFor="contact_person" className="block text-md font-semibold text-gray-800 mt-2">{t('Common.ContactPersonUpper')}</label>
                                <input
                                    readOnly={true}
                                    name='contact_person'
                                    type="text"
                                    value={company?.contact_person ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                        </div>
                        <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                            <div>
                                <label htmlFor="address" className="block text-md font-semibold text-gray-800">ADRESA</label>
                                <input
                                    readOnly={true}
                                    name='address'
                                    type="text"
                                    value={company?.address ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label htmlFor="fax" className="block text-md font-semibold text-gray-800">FAX</label>
                                <input
                                    readOnly={true}
                                    name='fax'
                                    type="text"
                                    value={company?.fax ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label htmlFor="web_address" className="block text-md font-semibold text-gray-800">WEB. ADRESA</label>
                                <input
                                    readOnly={true}
                                    name='web_address'
                                    type="text"
                                    value={company?.web_address ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label htmlFor="description" className="block text-md font-semibold text-gray-800">{t('Common.NoteUpper')}</label>
                                <textarea
                                    readOnly={true}
                                    name='description'
                                    type="text"
                                    value={company?.description ?? ''}
                                    className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-300">
                                <h3 className="text-base font-semibold text-gray-800 mb-3">{t('CalibrationCompanyList.Column.CompanyStatus')}</h3>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('Common.Status')}</label>
                                    <Dropdown
                                        id="company_validity"
                                        value={companyValidity}
                                        options={[{ label: t('Status.Active'), value: 1 }, { label: t('Status.Excluded'), value: 0 }]}
                                        onChange={(e) => setCompanyValidity(e.value)}
                                        className="w-full [&_.p-dropdown]:min-h-[2.75rem] [&_.p-dropdown]:w-full [&_.p-dropdown-label]:min-h-[2.75rem] [&_.p-dropdown-label]:py-2 [&_.p-dropdown-label]:text-base [&_.p-dropdown-label]:text-gray-800"
                                        style={{ minWidth: '100%' }}
                                        disabled={!user.permissions?.includes("edit calibration-company")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
